import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LocaleLink } from "@/components/LocaleLink";
import { useTranslation } from "react-i18next";
import { track } from "@/lib/analytics";

const SEEN_KEY = "aurora_changelog_seen_version";
const FRESH_WINDOW_DAYS = 7;
const VERSION_RE = /^##\s+(?:\[)?([0-9]{4}[.\-/][0-9]{1,2}[.\-/][0-9]{1,2})/m;

const parseLatestVersion = (markdownText) => {
  if (!markdownText) return null;
  const match = markdownText.match(VERSION_RE);
  return match?.[1]?.replace(/-/g, ".") || null;
};

const readSeenVersion = () => {
  try {
    return window.localStorage.getItem(SEEN_KEY);
  } catch (_) {
    return null;
  }
};

const markVersionAsSeen = (version) => {
  try {
    window.localStorage.setItem(SEEN_KEY, version);
  } catch (_) {
    // ignore storage errors
  }
};

const isFreshVersion = (version, seenVersion) => {
  if (!version) return false;
  if (seenVersion === version) return false;
  const dateOnly = version.replace(/\./g, "-");
  const released = new Date(dateOnly);
  if (Number.isNaN(released.getTime())) return false;
  const ageMs = Date.now() - released.getTime();
  const ageDays = ageMs / (1000 * 60 * 60 * 24);
  return ageDays <= FRESH_WINDOW_DAYS && ageDays >= -1;
};

/**
 * Tiny "What's new" pill that pulses when CHANGELOG.md got a new entry within
 * the last 7 days AND the current user hasn't visited /changelog since then.
 * Click marks it as seen via localStorage and never animates again for that
 * version. Renders nothing while loading or when nothing fresh is unseen.
 */
export const WhatsNewBadge = ({ className = "", variant = "desktop" }) => {
  const { t } = useTranslation("site");
  const [latest, setLatest] = useState(null);
  const [fresh, setFresh] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    fetch("/CHANGELOG.md", { cache: "no-cache", signal: controller.signal })
      .then((r) => (r.ok ? r.text() : ""))
      .then((md) => {
        if (cancelled || !md) return;
        const version = parseLatestVersion(md);
        if (!version) return;
        setLatest(version);
        const seenVersion = readSeenVersion();
        // localStorage unavailable -> readSeenVersion returns null, so this still works.
        setFresh(isFreshVersion(version, seenVersion));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const onClick = useCallback(() => {
    if (!latest) return;
    markVersionAsSeen(latest);
    setFresh(false);
    track("whats_new_click", { version: latest });
  }, [latest]);

  if (!latest) return null;

  const label = t("nav.whatsNew", "What's new");

  if (variant === "mobile") {
    return (
      <LocaleLink
        to="/changelog"
        onClick={onClick}
        data-testid="whats-new-mobile"
        className={`inline-flex items-center gap-2 text-white/80 ${className}`}
      >
        <span className="relative inline-flex">
          <span className="size-2 rounded-full bg-[var(--aurora-gold)]" />
          {fresh && (
            <span className="absolute inset-0 size-2 rounded-full bg-[var(--aurora-gold)] opacity-70 animate-ping" />
          )}
        </span>
        <span className="font-light">{label}</span>
      </LocaleLink>
    );
  }

  return (
    <LocaleLink
      to="/changelog"
      onClick={onClick}
      data-testid="whats-new-desktop"
      className={`relative hidden md:inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-[0.78rem] text-white/75 hover:text-white hover:border-white/30 transition ${className}`}
      aria-label={fresh ? `${label} — new` : label}
    >
      <span className="relative inline-flex">
        <span className="size-1.5 rounded-full bg-[var(--aurora-gold)]" />
        {fresh && (
          <span className="absolute -inset-0.5 rounded-full bg-[var(--aurora-gold)] opacity-60 animate-ping" />
        )}
      </span>
      <span>{label}</span>
    </LocaleLink>
  );
};
